import React from "react";
import { Button, Checkbox, DatePicker, Form, Input, message, Typography } from "antd";
import HeaderComponent from "../component/header";
import { api_save_policy_renewal } from "../api/policyrenewal";
import { DashboardData, PolicyRenewalSaveRequest } from "../constant";

const { Title } = Typography;

const PolicyRenewalPage: React.FC = () => {
  const [form] = Form.useForm();

  const boolFields = [
    "acupuncture",
    "automated_spinal_decompression",
    "colon_irrigation",
    "craniosacral_therapy",
    "cupping",
    "dot_exams",
    "drugs_prescribed_or_injected",
    "dry_needling",
    "foot_bath",
    "functional_medicine",
    "gynecology_exams",
    "hair_analysis",
    "homeopathy",
    "hyperbaric_oxygen_therapy",
    "iodbs",
    "invasive_emg",
    "iridology",
    "minor_surgery",
    "mua",
    "obstetrics",
    "red_light_therapy",
    "sauna",
    "treatment_diagnosis",
    "urinalysis",
    "vitamin_injections",
    "otherchiro",
    "massage",
    "acupuncturist",
    "physical_therapist",
    "doula",
    "midwife",
    "medical_doctor",
    "other",
  ] as const;

  const getDashboardDataFromLocalStorage = (): DashboardData => {
    const raw = localStorage.getItem("dashboard_data");
    if (!raw) {
      return {};
    }

    try {
      return JSON.parse(raw) as DashboardData;
    } catch {
      return {};
    }
  };

  const onSubmit = async (values: any) => {
    const fileid = Number(localStorage.getItem("fileid"));
    const token = localStorage.getItem("token");

    if (!token || Number.isNaN(fileid)) {
      message.error("Your session is invalid. Please login again.");
      return;
    }

    const payload: PolicyRenewalSaveRequest = {
      fileid,
      hours_per_week: Number(values.hours_per_week),
      initial1: values.initial1,
      initial2: values.initial2,
      initial3: values.initial3,
      initial4: values.initial4,
      signed: values.signed,
      signed_date: values.signed_date ? values.signed_date.toISOString() : undefined,
      dashboard: getDashboardDataFromLocalStorage(),
      acupuncture: false,
      automated_spinal_decompression: false,
      colon_irrigation: false,
      craniosacral_therapy: false,
      cupping: false,
      dot_exams: false,
      drugs_prescribed_or_injected: false,
      dry_needling: false,
      foot_bath: false,
      functional_medicine: false,
      gynecology_exams: false,
      hair_analysis: false,
      homeopathy: false,
      hyperbaric_oxygen_therapy: false,
      iodbs: false,
      invasive_emg: false,
      iridology: false,
      minor_surgery: false,
      mua: false,
      obstetrics: false,
      red_light_therapy: false,
      sauna: false,
      treatment_diagnosis: false,
      urinalysis: false,
      vitamin_injections: false,
      otherchiro: false,
      massage: false,
      acupuncturist: false,
      physical_therapist: false,
      doula: false,
      midwife: false,
      medical_doctor: false,
      other: false,
    };

    boolFields.forEach((fieldName) => {
      payload[fieldName] = Boolean(values[fieldName]);
    });

    message.loading({ content: "Saving policy renewal form...", key: "saving", duration: 0 });
    const response = await api_save_policy_renewal(payload);
    if (response.bSuccess) {
      message.success({ content: "Policy renewal form saved successfully.", key: "saving", duration: 3 });
      return;
    }
    message.error({ content: response.msg || "Failed to save policy renewal form.", key: "saving", duration: 5 });
  };

  const onFinishFailed = (errorInfo: any) => {
    alert("Please check the form fields and try again.");
  };


  return (
    <div>
      <HeaderComponent />
      <div className='max-w-screen-xl mx-auto p-10'>

        <div className='flex flex-col mb-5'>
          <Title level={4} className="flex justify-center">POLICY RENEWAL: INSURED REPRESENTATION STATEMENT</Title>
        </div>

        <Form
          form={form}
          layout="horizontal"
          onFinish={onSubmit}
          onFinishFailed={onFinishFailed}
        >
          <div className='mb-5 grid grid-cols-2 sm:grid-cols-6 gap-2'>
            <div className='col-span-2 sm:col-span-6 lg:col-span-6'>
              <Form.Item label="How many hours per week are you practicing? (includes all patient related care):" name="hours_per_week" rules={[{ required: true }]} className="mb-0">
                <Input className="h-6 w-16" type="number" min={0} />
              </Form.Item>
            </div>

            <div className='col-span-2 sm:col-span-6 lg:col-span-3'>
              <div className="col-span-2 sm:col-span-6 lg:col-span-6 text-style">
                What procedures other than straight adjustments are done by you:
              </div>
              <div className='col-span-2 sm:col-span-6 lg:col-span-6'>
                <Form.Item  name="acupuncture" valuePropName="checked" className="mb-0 ml-8">
                  <Checkbox>Acupuncture</Checkbox>
                </Form.Item>
                <Form.Item  name="automated_spinal_decompression" valuePropName="checked" className="mb-0 ml-8">
                  <Checkbox>Automated Spinal Decompression</Checkbox>
                </Form.Item>
                <Form.Item  name="colon_irrigation" valuePropName="checked" className="mb-0 ml-8">
                  <Checkbox>Colon Irrigation</Checkbox>
                </Form.Item>
                <Form.Item  name="craniosacral_therapy" valuePropName="checked" className="mb-0 ml-8">
                  <Checkbox>Craniosacral Therapy</Checkbox>
                </Form.Item>
                <Form.Item  name="cupping" valuePropName="checked" className="mb-0 ml-8">
                  <Checkbox>Cupping</Checkbox>
                </Form.Item>
                <Form.Item  name="dot_exams" valuePropName="checked" className="mb-0 ml-8">
                  <Checkbox>DOT Exams</Checkbox>
                </Form.Item>
                <Form.Item  name="drugs_prescribed_or_injected" valuePropName="checked" className="mb-0 ml-8">
                  <Checkbox>Drugs prescribed or injected</Checkbox>
                </Form.Item>
                <Form.Item  name="dry_needling" valuePropName="checked" className="mb-0 ml-8">
                  <Checkbox>Dry Needling</Checkbox>
                </Form.Item>
                <Form.Item  name="foot_bath" valuePropName="checked" className="mb-0 ml-8">
                  <Checkbox>Foot Bath</Checkbox>
                </Form.Item>
                <Form.Item  name="functional_medicine" valuePropName="checked" className="mb-0 ml-8">
                  <Checkbox>Functional Medicine</Checkbox>
                </Form.Item>
                <Form.Item  name="gynecology_exams" valuePropName="checked" className="mb-0 ml-8">
                  <Checkbox>Gynecology Exams</Checkbox>
                </Form.Item>
                <Form.Item  name="hair_analysis" valuePropName="checked" className="mb-0 ml-8">
                  <Checkbox>Hair Analysis</Checkbox>
                </Form.Item>
                <Form.Item  name="homeopathy" valuePropName="checked" className="mb-0 ml-8">
                  <Checkbox>Homeopathy</Checkbox>
                </Form.Item>
                <Form.Item  name="hyperbaric_oxygen_therapy" valuePropName="checked" className="mb-0 ml-8">
                  <Checkbox>Hyperbaric Oxygen Therapy</Checkbox>
                </Form.Item>
                <Form.Item  name="iodbs" valuePropName="checked" className="mb-0 ml-8">
                  <Checkbox>Interpretation of Diagnostic Blood Studies</Checkbox>
                </Form.Item>
                <Form.Item  name="invasive_emg" valuePropName="checked" className="mb-0 ml-8">
                  <Checkbox>Invasive EMG</Checkbox>
                </Form.Item>
                <Form.Item  name="iridology" valuePropName="checked" className="mb-0 ml-8">
                  <Checkbox>Iridology</Checkbox>
                </Form.Item>
                <Form.Item  name="minor_surgery" valuePropName="checked" className="mb-0 ml-8">
                  <Checkbox>Minor Surgery</Checkbox>
                </Form.Item>
                <Form.Item  name="mua" valuePropName="checked" className="mb-0 ml-8">
                  <Checkbox>MUA (manipulation under anesthesia)</Checkbox>
                </Form.Item>
                <Form.Item  name="obstetrics" valuePropName="checked" className="mb-0 ml-8">
                  <Checkbox>Obstetrics</Checkbox>
                </Form.Item>
                <Form.Item  name="red_light_therapy" valuePropName="checked" className="mb-0 ml-8">
                  <Checkbox>Red Light Therapy </Checkbox>
                </Form.Item>
                <Form.Item  name="sauna" valuePropName="checked" className="mb-0 ml-8">
                  <Checkbox>Sauna</Checkbox>
                </Form.Item>
                <Form.Item  name="treatment_diagnosis" valuePropName="checked" className="mb-0 ml-8">
                  <Checkbox>Treatment or Diagnosis of Disease other than N.M.S</Checkbox>
                </Form.Item>
                <Form.Item  name="urinalysis" valuePropName="checked" className="mb-0 ml-8">
                  <Checkbox>Urinalysis</Checkbox>
                </Form.Item>
                <Form.Item  name="vitamin_injections" valuePropName="checked" className="mb-0 ml-8">
                  <Checkbox>Vitamin Injections</Checkbox>
                </Form.Item>
              </div>
            </div>
            <div className="col-span-2 sm:col-span-6 lg:col-span-3 text-style">
              <div className="col-span-2 sm:col-span-6 lg:col-span-6 text-style">
                Are you sharing space with any other Health Care Providers ?*** :
              </div>
              <div className='col-span-2 sm:col-span-6 lg:col-span-6'>
                <Form.Item  name="otherchiro" valuePropName="checked" className="mb-0 ml-8">
                  <Checkbox>Other Chiropracto</Checkbox>
                </Form.Item>
              </div>
              <div className='col-span-2 sm:col-span-6 lg:col-span-6'>
                <Form.Item  name="massage" valuePropName="checked" className="mb-0 ml-8">
                  <Checkbox>Licensed Massage Therapist</Checkbox>
                </Form.Item>
              </div>
              <div className='col-span-2 sm:col-span-6 lg:col-span-6'>
                <Form.Item  name="acupuncturist" valuePropName="checked" className="mb-0 ml-8">
                  <Checkbox>Acupuncturist</Checkbox>
                </Form.Item>
              </div>
              <div className='col-span-2 sm:col-span-6 lg:col-span-6'>
                <Form.Item  name="physical_therapist" valuePropName="checked" className="mb-0 ml-8">
                  <Checkbox>Physical Therapist</Checkbox>
                </Form.Item>
              </div>
              <div className='col-span-2 sm:col-span-6 lg:col-span-6'>
                <Form.Item  name="doula" valuePropName="checked" className="mb-0 ml-8">
                  <Checkbox>Doula</Checkbox>
                </Form.Item>
              </div>
              <div className='col-span-2 sm:col-span-6 lg:col-span-6'>
                <Form.Item  name="midwife" valuePropName="checked" className="mb-0 ml-8">
                  <Checkbox>Midwife</Checkbox>
                </Form.Item>
              </div>
              <div className='col-span-2 sm:col-span-6 lg:col-span-6'>
                <Form.Item  name="medical_doctor" valuePropName="checked" className="mb-0 ml-8">
                  <Checkbox>Medical Doctor</Checkbox>
                </Form.Item>
              </div>
              <div className='col-span-2 sm:col-span-6 lg:col-span-6'>
                <Form.Item  name="other" valuePropName="checked" className="mb-0 ml-8">
                  <Checkbox>Other</Checkbox>
                </Form.Item>
              </div>
            </div>
            
          </div>
          <div className='flex flex-col'>
            <Title level={5} className="italic">
              Important: If you are unable to complete the below questions due to changes in your practice or status please call our office at 800-219-9090 as soon as possible 
            </Title>
            <p className="text-sm">
              I understand that: (1) my renewal policy is issued based upon reliance of the following representations; and,
              (2) any policy obtained by fraud, material misrepresentation or omission is null and void. 
            </p>
            <p className="text-sm">
              In consideration of renewal of my policy, since my ChiroFutures malpractice policy was issued last year:
            </p>
          </div>

          <div className="flex justify-center">
            <table style={{ border: "solid 1px black", width: "95%" }}>
                <tr className="h-20" style={{ border: "solid 1px black"}}>
                    <td className ="mt-2 ml-6" style={{ border: "solid 1px black", width: "80%" }}>
                        <div className="mt-2 ml-6 text-2sm">
                            1. There have been NO changes within my practice (i.e., formed new legal entity, added a new chiropractor, changed practice tools/procedures,
                            number of hours worked, OR now sharing space with/allowing another Health Care Professional to use my office, etc.) 
                        </div>
                    </td>
                    <td className="flex flex-col text-center items-center">
                        <div className="flex justify-center mt-3 text-xs">
                            Please initial
                        </div>
                        
                        <div className="flex justify-center mt-1 text-xs w-[60%]">
                            <Form.Item
                                name="initial1"
                                rules={[
                                    { required: true, message: "Please enter your initials" },
                                    { max: 4, message: "Initials should be 2–4 characters" },
                                ]}
                                className="mb-0"
                                >
                                <Input
                                    maxLength={4}
                                    style={{ textTransform: "uppercase" }}
                                    className="h-6"
                                />
                            </Form.Item>
                        </div>
                    </td>
                </tr> 
                <tr  className="h-20" style={{ border: "solid 1px black"}}>
                    <td className ="mt-2 ml-6" style={{ border: "solid 1px black", width: "80%" }}>
                        <div className="mt-2 ml-6 text-2sm">
                            2. I have had NO interaction with any Chiropractic Board other than my routine license renewal. This would include any board inquiries, complaints filed,
                            license investigations (closed, open or pending), and/or received a letter of concern/warning, stipulation, probation, and/or suspension. 
                        </div>
                    </td>
                    <td className="flex flex-col text-center items-center">
                        <div className="flex justify-center mt-3 text-xs">
                            Please initial
                        </div>
                        <div className="flex justify-center mt-2 text-xs w-[60%]">
                            <Form.Item
                                name="initial2"
                                rules={[
                                    { required: true, message: "Please enter your initials" },
                                    { max: 4, message: "Initials should be 2–4 characters" },
                                ]}
                                className="mb-0"
                                >
                                <Input
                                    maxLength={4}
                                    style={{ textTransform: "uppercase" }}
                                    className="h-6"
                                />
                            </Form.Item>
                        </div>
                    </td>
                </tr> 
                <tr  className="h-20" style={{ border: "solid 1px black"}}>
                    <td className ="mt-2 ml-6" style={{ border: "solid 1px black", width: "80%" }}>
                        <div className="mt-2 ml-6 text-2sm">
                            3. I have NOT been convicted of, plead guilty or no contest to a crime (including DUI/major traffic violation), or any other violation of a law or ordinance. 
                        </div>
                    </td>
                    <td className="flex flex-col text-center items-center">
                        <div className="flex justify-center mt-3 text-xs">
                            Please initial
                        </div>
                        <div className="flex justify-center mt-2 text-xs w-[60%]">
                            <Form.Item
                                name="initial3"
                                rules={[
                                    { required: true, message: "Please enter your initials" },
                                    { max: 4, message: "Initials should be 2–4 characters" },
                                ]}
                                className="mb-0"
                                >
                                <Input
                                    maxLength={4}
                                    style={{ textTransform: "uppercase" }}
                                    className="h-6"
                                />
                            </Form.Item>
                        </div>
                    </td>
                </tr> 
                <tr  className="h-20" style={{ border: "solid 1px black"}}>
                    <td className ="mt-2 ml-6" style={{ border: "solid 1px black", width: "80%" }}>
                        <div className="mt-2 ml-6 text-2sm">
                            4. Other than those previously reported, I am NOT aware of any claims, incidents, wrongful acts, licensure or accreditation actions open, closed or pending against me. 
                        </div>
                    </td>
                    <td className="flex flex-col text-center items-center">
                        <div className="flex justify-center mt-3 text-xs">
                            Please initial
                        </div>
                        <div className="flex justify-center mt-2 text-xs w-[60%]">
                            <Form.Item
                                name="initial4"
                                rules={[
                                    { required: true, message: "Please enter your initials" },
                                    { max: 4, message: "Initials should be 2–4 characters" },
                                ]}
                                className="mb-0"
                                >
                                <Input
                                    maxLength={4}
                                    style={{ textTransform: "uppercase" }}
                                    className="h-6"
                                />
                            </Form.Item>
                        </div>
                    </td>
                </tr> 
            </table>
          </div>

          <div className='grid grid-cols-2 sm:grid-cols-6 gap-2 mt-4'>
            <div className='col-span-6'>
              <p>To the best of my knowledge and belief, the representations set forth herein are true and complete. </p>
            </div>
            <div className='col-span-2 sm:col-span-3 lg:col-span-3'>
              <Form.Item label="Signed:" name="signed" rules={[{ required: true }]} className="mb-0 ml-10">
                <Input className="h-6"/>
              </Form.Item>
            </div>

            <div className='col-span-2 sm:col-span-3 lg:col-span-3 ml-5'>
              <Form.Item label="Date:" name="signed_date" rules={[{ required: true }]} className="mb-0">
                <DatePicker className="h-6 w-32" />
              </Form.Item>
            </div>
            <div className='col-span-6'>
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <Button type="primary" htmlType="submit">Submit</Button>
              </div>
            </div>
          </div>
        </Form>
      </div>
    </div>
  );
};

export default PolicyRenewalPage;